import { logError } from '~shared/observability/observability';
import { Offer } from '~entities/offer/model/offer.types';
import { CartItem, LastBooking, Session } from '../model/session.types';
import { InsuranceOffer } from '~entities/insurance-offer/model/insurance-offer.types';
import { ExtraService } from '~entities/extra-service/model/extra-service.types';
import { User } from '~entities/user/model/user.types';
import type {
  AddOfferCommand,
  LastBookingDTO,
  OneSessionDTO,
  OneSessionItemDTO,
  UpdateItemCommand,
} from '~shared/api/schemas';
import {
  assertMoneyReported,
  reportMoney,
  toMoney,
  type Money,
} from '~shared/api/assert-money';
import { SessionSchema } from '../model/session.contracts';

/**
 * Money the guest is shown but is not charged for on this screen: insurance premiums and
 * extra-service prices. Report and keep going — a malformed premium must not take the whole
 * session down, and every charged amount (total, deposit, item prices) is guarded by a throw
 * below (samera/samera#3319).
 */
const displayedPrice = (value: Money, context: string): Money => {
  reportMoney(value, context);
  // Rebuilt even when the guard reports: a money object assigned wholesale leaks whatever the
  // backend adds to it, which is the leak the explicit mappers exist to close. Unlike the calendar
  // teaser, the value is kept rather than dropped — a session price that vanishes breaks the cart
  // layout, while a wrong one is at least visible and reported.
  return toMoney(value);
};

/**
 * A guest the backend typed as CHILD but sent without an age.
 *
 * The deleted Zod DTO modelled guests as a union where `childAge` was REQUIRED for CHILD, so this
 * response failed the parse and took the whole session down. The wire contract is wider —
 * `GuestDTO.childAge` is optional — and throwing away a guest's cart over it is the wrong trade.
 * But it is not harmless either: transformCartItemToBookingRoom coalesces a missing age to `0`
 * (offer.mappers.ts), so the next availability request silently asks for a newborn. Report it and
 * let the existing coalescing stand (samera/samera#3319).
 */
const reportedChildAge = (
  guest: OneSessionItemDTO['guests'][number],
  context: string,
): number | undefined => {
  if (guest.type === 'CHILD' && guest.childAge === undefined) {
    logError(new Error('session guest of type CHILD has no childAge'), {
      kind: 'session-contract',
      sessionContext: context,
    });
  }
  return guest.childAge;
};

const toUser = (dto: OneSessionDTO['user']): User => ({
  salutation: dto.salutation,
  title: dto.title,
  firstname: dto.firstname,
  lastname: dto.lastname,
  email: dto.email,
  street: dto.street,
  zipcode: dto.zipcode,
  city: dto.city,
  // The generated type narrows this to the ISO country enum; the domain keeps a plain string,
  // because the select is fed from `configuration.countries`, whose ids are plain strings.
  country: dto.country,
  phone: dto.phone,
});

const toInsuranceOffer = (
  dto: OneSessionDTO['erv']['offers'][number],
  index: number,
): InsuranceOffer => ({
  id: dto.id,
  name: dto.name,
  selected: dto.selected,
  specification: dto.specification,
  description: dto.description,
  totalPremium: displayedPrice(
    dto.totalPremium,
    `session.erv.offers[${index}].totalPremium`,
  ),
  totalPremiumText: dto.totalPremiumText,
  agreements: dto.agreements.map(agreement => ({
    key: agreement.key,
    mandatory: agreement.mandatory,
    // Honestly optional on the wire (a hotel can leave the copy empty), required by the domain
    // type and rendered directly. The deleted Zod schema declared them required, so an empty one
    // used to fail the whole parse; coalescing renders an empty checkbox label instead.
    agreementText: agreement.agreementText ?? '',
    agreementTextHtml: agreement.agreementTextHtml ?? '',
  })),
  documents: dto.documents.map(document => ({
    key: document.key,
    url: document.url,
    label: document.label ?? '',
    text: document.text ?? '',
  })),
});

/**
 * `ExtraServiceDTO.person.type` is a bare `string` in the generated contract (the backend models
 * it as an untyped discriminator), but the domain — and the price-basis label in the UI — only
 * knows ADULT and CHILD. Narrow with a check rather than a cast, so an unknown value drops the
 * person block instead of reaching the UI as a broken label.
 */
const toExtraServicePerson = (
  person: NonNullable<OneSessionItemDTO['extraServices'][number]['person']>,
): ExtraService['person'] => {
  if (person.type !== 'ADULT' && person.type !== 'CHILD') return undefined;
  return {
    type: person.type,
    // Rebuilt, not assigned: the wire declares exactly `from` and `to`, and the deleted Zod
    // schema stripped anything else. A wholesale assign would newly leak whatever the backend
    // adds to it into Redux and the query cache.
    age: person.age ? { from: person.age.from, to: person.age.to } : person.age,
  };
};

const toExtraService = (
  dto: OneSessionItemDTO['extraServices'][number],
  context: string,
): ExtraService => ({
  id: dto.id,
  extraServiceId: dto.extraServiceId,
  quantity: dto.quantity,
  possibleQuantities: dto.possibleQuantities,
  price: displayedPrice(dto.price, `${context}.price`),
  pricePerNight: dto.pricePerNight
    ? displayedPrice(dto.pricePerNight, `${context}.pricePerNight`)
    : undefined,
  totalPrice: displayedPrice(dto.totalPrice, `${context}.totalPrice`),
  person: dto.person ? toExtraServicePerson(dto.person) : undefined,
  mandatory: dto.mandatory,
});

const toCartItem = (dto: OneSessionItemDTO, index: number): CartItem => ({
  id: dto.id,
  type: dto.type,
  arrivalDay: dto.arrivalDay,
  nights: dto.nights,
  guests: dto.guests.map((guest, guestIndex) => ({
    id: guest.id,
    type: guest.type,
    childAge: reportedChildAge(
      guest,
      `session.items[${index}].guests[${guestIndex}]`,
    ),
    birthdate: guest.birthdate,
    firstname: guest.firstname,
    lastname: guest.lastname,
  })),
  price: toMoney(dto.price),
  comment: dto.comment,
  overlayMessages: dto.overlayMessages,
  bookingMessages: dto.bookingMessages,
  // Required on the wire, optional in the domain — and it must STAY optional. Only an explicit
  // false hides the booking CTA; `undefined` (a backend that predates samera/samera#3217) keeps
  // both CTAs. Do not "tidy" CartItemSchema to required.
  bookable: dto.bookable,
  alternativeRates: dto.alternativeRates.map(rate => ({
    rateId: rate.rateId,
    price: toMoney(rate.price),
    overlayMessages: rate.overlayMessages,
    bookingMessages: rate.bookingMessages,
    bookable: rate.bookable,
  })),
  extraServices: dto.extraServices.map((extraService, extraServiceIndex) =>
    toExtraService(
      extraService,
      `session.items[${index}].extraServices[${extraServiceIndex}]`,
    ),
  ),
  rateId: dto.rateId,
  roomId: dto.roomCategoryId,
  lastMinutePackageId: dto.lastMinutePackageId,
  packageId: dto.packageGroupId,
  // Rebuilt, not assigned: the deleted Zod schema declared exactly these three fields and
  // stripped the rest, so a wholesale assign would newly leak anything the backend adds to
  // packageDetails into Redux and the query cache.
  packageDetails: dto.packageDetails
    ? {
        id: dto.packageDetails.id,
        name: dto.packageDetails.name,
        includedServices: dto.packageDetails.includedServices,
      }
    : undefined,
  // `variantId` is on the wire but not in the domain type, and `packageCategoryId` is in the
  // domain type but never sent for a cart item. Both are excluded on purpose.
});

/**
 * Build the domain Session from the wire DTO.
 *
 * Written out field by field rather than spread. The deleted Zod layer stripped every undeclared
 * key, so a spread would newly leak `campaign`, the session `comment`, `variantId` and the four
 * extra `deposit` fields into Redux, the query cache and the devtools (samera/samera#3319).
 */
/**
 * Report-only structural check of this mapper's own output.
 *
 * `Session` is `z.infer<typeof SessionSchema>`, so the compiler already proves the mapper builds
 * the right SHAPE. What it cannot prove is that the wire DATA matches the generated types: the
 * spec says `guests[].type` is a string and `bookable` is a boolean, and nothing at runtime
 * checks that the backend agrees. That runtime check is exactly what the deleted Zod DTO layer
 * did, and this is the thin report-only remainder of it, moved to the domain boundary
 * (samera/samera#3319).
 *
 * Report-only on purpose, in two ways. It never throws, because a shape complaint must not take
 * a guest's cart down. And it returns the ORIGINAL object, never `result.data` — parsing would
 * strip and coerce, which would make this guard change behaviour instead of observing it.
 *
 * Only `path` and `code` are reported. An `invalid_enum_value` message embeds the rejected value
 * verbatim ("... received 'X'"), and this object carries the guest's name, email and phone, so
 * `code` is the field that cannot leak one into an error report.
 */
const reportSessionShape = (session: Session): Session => {
  const result = SessionSchema.safeParse(session);
  if (!result.success) {
    logError(
      new Error('session mapper output does not satisfy SessionSchema'),
      {
        kind: 'session-contract',
        issues: result.error.issues
          .map(issue => `${issue.path.join('.') || '<root>'}: ${issue.code}`)
          .join('; '),
      },
    );
  }
  return session;
};

export const transformSessionDtoToSession = (dto: OneSessionDTO): Session => {
  // Charged money throws: the cart total, the deposit the guest pays now, and every item price
  // are what the PSP is asked to capture. Rendering a wrong one is the worst failure mode here.
  assertMoneyReported(dto.total, 'session.total');
  assertMoneyReported(
    dto.deposit.depositAmount,
    'session.deposit.depositAmount',
  );
  dto.items.forEach((item, index) => {
    assertMoneyReported(item.price, `session.items[${index}].price`);
    item.alternativeRates.forEach((rate, rateIndex) =>
      assertMoneyReported(
        rate.price,
        `session.items[${index}].alternativeRates[${rateIndex}].price`,
      ),
    );
  });

  return reportSessionShape({
    sessionId: dto.sessionId,
    user: toUser(dto.user),
    erv: { offers: dto.erv.offers.map(toInsuranceOffer) },
    cart: dto.items.map(toCartItem),
    total: toMoney(dto.total),
    deposit: {
      depositPercentage: dto.deposit.depositPercentage,
      depositAmount: toMoney(dto.deposit.depositAmount),
    },
    promotionCode: dto.promotion?.code,
  });
};

export const transformOfferToAddToCartDto = (offer: Offer): AddOfferCommand => {
  // TODO: in future no throw errors, make more type safe api
  if (
    !offer.type ||
    !offer.adults ||
    !offer.children ||
    !offer.price ||
    !offer.rateId ||
    !offer.roomId
  ) {
    const missingFields: string[] = [];
    if (!offer.type) missingFields.push('type');
    if (!offer.adults) missingFields.push('adults');
    if (!offer.children) missingFields.push('children');
    if (!offer.price) missingFields.push('price');
    if (!offer.rateId) missingFields.push('rateId');
    if (!offer.roomId) missingFields.push('roomId');

    const message = `Invalid offer: missing ${missingFields.join(', ')}`;
    logError(new Error(message), {
      // SigNoz has no fingerprint concept, so grouping happens at query time
      // on error.fingerprint. logError derives it from the message, which also
      // covers the unhandled rejection from the throw below — that arrives as
      // a separate record and would otherwise group by its own message.
      kind: 'invalid-offer',
      missingOfferFields: missingFields.join(','),
      offer: { ...offer } as Record<string, unknown>,
    });
    throw new Error(message);
  }

  return {
    type: offer.type,
    arrivalDay: offer.arrivalDay,
    nights: offer.nights,
    adults: offer.adults,
    children: offer.children,
    price: offer.price,

    rateId: offer.rateId,
    roomCategoryId: offer.roomId,

    lastMinutePackageId: offer.lastMinutePackageId,
    packageGroupId: offer.packageId,
    // No `packageCategoryId`: AddOfferCommand does not declare one, so the backend has always
    // ignored the field the old Zod DTO sent (samera/samera#3319).
  };
};

/**
 * The old DTO schema spread the whole cart item and let Zod strip it down. UpdateItemCommand is
 * much narrower than a CartItem, so the fields it does not declare are selected out here instead;
 * the backend ignored them either way.
 */
export const transformCartItemToUpdateCartItemDto = (
  cartItem: CartItem,
): UpdateItemCommand => ({
  id: cartItem.id,
  rateId: cartItem.rateId,
  price: cartItem.price,
  comment: cartItem.comment,
  guests: cartItem.guests.map(guest => ({
    id: guest.id,
    birthdate: guest.birthdate,
    firstname: guest.firstname,
    lastname: guest.lastname,
  })),
  extraServices: (cartItem.extraServices ?? []).map(extraService => ({
    id: extraService.id,
    quantity: extraService.quantity,
  })),
});

export const transformLastBookingDtoToLastBooking = (
  lastBookingDto: LastBookingDTO,
): LastBooking => {
  // This endpoint moved off the Zod responseContract to the typed client, which
  // only guarantees SHAPE at compile time — nothing checks the values on the
  // wire. Every money field here is user-facing AND feeds the GTM purchase event
  // (build-purchase-data-layer.ts), so an amount arriving as null or "129.00"
  // would silently become a wrong displayed price and a wrong conversion value.
  //
  // Reported explicitly rather than relying on the throw surfacing by itself:
  // the error boundary's production logError is still a TODO, so an unreported
  // contract break would be invisible. lastBookingQueryOptions additionally sets
  // throwOnError for this error type, so the boundary actually renders.
  assertMoneyReported(lastBookingDto.total, 'lastBooking.total');
  assertMoneyReported(
    lastBookingDto.depositAmount,
    'lastBooking.depositAmount',
  );
  lastBookingDto.items.forEach((item, index) =>
    assertMoneyReported(item.price, `lastBooking.items[${index}].price`),
  );

  // Rebuilt field by field, not spread: the Zod responseContract that used to strip
  // unknown keys is gone (#3319), so a spread now copies whatever the backend adds
  // straight into the React Query cache and devtools. Same rebuild pattern as
  // transformBookingDtoToBookingDetail (booking.mappers.ts) — the two domain types
  // themselves differ (LastBooking carries no guest fields, LastBookingItem no stay
  // dates), so this mirrors the *shape of the fix*, not a shared schema.
  //
  // `LastBookingSchema.parse(lastBookingDto)` would strip recursively for free, but
  // is deliberately not used here: it throws a ZodError, and lastBookingQueryOptions
  // (session.queries.ts) narrows throwOnError to MoneyContractError only — a
  // legitimate 404 (no booking yet) must not crash the page. A ZodError would sit
  // unread in query state and the payment-result page would render success with no
  // amounts and no purchase event (samera/samera#3514).
  return {
    bookingId: lastBookingDto.bookingId,
    id: lastBookingDto.id,
    status: lastBookingDto.status,
    total: toMoney(lastBookingDto.total),
    depositAmount: toMoney(lastBookingDto.depositAmount),
    items: lastBookingDto.items.map(item => ({
      roomCategoryId: item.roomCategoryId,
      roomCategoryName: item.roomCategoryName,
      ratePlanId: item.ratePlanId,
      packageGroupId: item.packageGroupId,
      price: toMoney(item.price),
    })),
  };
};
